import React, { useEffect, createContext, useMemo, useState, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom-v5-compat';

import { noop } from 'ui-components/default-props/noop';

import { PagingContext } from '../../../contexts/paging-context';
import { useEnableStockAudit } from '../../../feature-flags/hooks/use-enable-stock-audit';
import { useQuickSearchTerm } from '../../../hooks/routing/use-quick-search-term';
import { usePusherEvent } from '../../../hooks/use-pusher-event';
import { createLookupHash } from '../../../services/hash-service';
import { INVENTORY_ACTION_COMPLETE } from '../../../services/pusher-service/pusher-contract-messages';
import { inventoryEntitiesLoad } from '../../../store/entities/entities.actions';
import Global from '../../../store/global-selectors';
import { getInventory, getInventoryWarehouseLocations } from '../../../store/inventory/inventory.api';
import { updateSellerInventoryWarehousesLocations } from '../../../store/seller/seller.actions';

const InventoryContext = createContext();
const useInventoryContext = () => {
  return useContext(InventoryContext);
};

const InventoryContextProvider = ({ children = null }) => {
  const dispatch = useDispatch();
  const { page, pageSize } = useContext(PagingContext);
  const isStockAuditEnabled = useEnableStockAudit();
  const [entitiesLoading, setEntitiesLoading] = useState(false);
  const [inventoryIds, setInventoryIds] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [orderBy, setOrderBy] = useState('SKU');
  const [direction, setDirection] = useState('descending');
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [appliedFilters, setAppliedFilters] = useState([]);

  const onFiltersChanged = useCallback((filtersToApply = []) => {
    setAppliedFilters(filtersToApply);
  }, []);

  const searchTerm = useQuickSearchTerm();

  const { inventoryWarehouseId } = useParams();

  const refreshWarehouseLocations = useCallback(() => {
    setEntitiesLoading(true);

    getInventoryWarehouseLocations()
      .promise.then(({ data, errors }) => {
        if (!errors?.length > 0) {
          dispatch(updateSellerInventoryWarehousesLocations(data));
        }
      })
      .catch(noop)
      .finally(() => setEntitiesLoading(false));
  }, [dispatch]);

  const refreshGridData = useCallback(() => {
    setEntitiesLoading(true);

    const mergedFilters = [...appliedFilters];

    if (inventoryWarehouseId) {
      mergedFilters.push({
        filterById: 'inventoryWarehouseId',
        type: 'TYPE_STRING',
        filterValue: 'InventoryWarehouseId',
        criterionArgumentValue: inventoryWarehouseId,
      });
    }

    const inventoryEntitiesPromise = getInventory({
      pageNumber: page,
      pageSize,
      orderBy,
      direction,
      searchTerm,
      appliedFilters: mergedFilters,
      includeAuditData: isStockAuditEnabled,
    });
    inventoryEntitiesPromise.promise
      .then(({ inventory = [], ...pageData }) => {
        const newInventory = inventory.map((inv) => {
          return {
            ...inv,
            id: `${inv.productId}_${inv.inventoryLocationId}`,
          };
        });
        dispatch(
          inventoryEntitiesLoad({
            entities: {
              inventory: createLookupHash(newInventory || [], 'id'),
            },
          }),
        );
        setInventoryIds(pageData.productIds || []);
        setTotalRecords(pageData.totalCount);
        setEntitiesLoading(false);
      })
      .catch(() => {
        setInventoryIds([]);
        setTotalRecords(0);
        setEntitiesLoading(false);
      });
    return inventoryEntitiesPromise;
  }, [appliedFilters, inventoryWarehouseId, page, pageSize, orderBy, direction, searchTerm, isStockAuditEnabled, dispatch]);

  const currentUserId = useSelector(Global.selectUserCurrentUserId);
  const closeAfterPusherResponse = useCallback(
    (pusherMessage) => {
      if (pusherMessage && pusherMessage.UserID === currentUserId && pusherMessage.Success) {
        refreshGridData();
      }
    },
    [currentUserId, refreshGridData],
  );
  usePusherEvent(INVENTORY_ACTION_COMPLETE, closeAfterPusherResponse);

  useEffect(() => {
    const inventoryEntitiesPromise = refreshGridData();
    return () => inventoryEntitiesPromise?.cancel();
  }, [refreshGridData]);

  const setColumnSort = useCallback((columnId, sortDirection) => {
    setOrderBy(columnId);
    setDirection(sortDirection);
  }, []);

  const clearFilters = useCallback(() => {
    onFiltersChanged([]);
  }, [onFiltersChanged]);

  const value = useMemo(
    () => ({
      entitiesLoading,
      inventoryIds,
      totalRecords,
      setColumnSort,
      selectedProductIds,
      setSelectedProductIds,
      appliedFilters,
      onFiltersChanged,
      clearFilters,
      refreshGridData,
      refreshWarehouseLocations,
      inventoryWarehouseId,
    }),
    [
      entitiesLoading,
      inventoryIds,
      totalRecords,
      setColumnSort,
      selectedProductIds,
      setSelectedProductIds,
      appliedFilters,
      onFiltersChanged,
      clearFilters,
      refreshGridData,
      refreshWarehouseLocations,
      inventoryWarehouseId,
    ],
  );

  return <InventoryContext.Provider value={value}>{children}</InventoryContext.Provider>;
};

InventoryContextProvider.propTypes = {
  children: PropTypes.node,
};

export { InventoryContext, useInventoryContext };

export default InventoryContextProvider;
